import { NEW_CARD, PaymentMethodType, PaymentProvider, ProcessPaymentOptions } from '../../typings/Payment'
import { getGoogleSearchParam } from '../../lib/get-google-search-param'

const confirmPaymentIntent = async (
  paymentProvider: PaymentProvider,
  {
    clientSecret,
    user,
    selectedPaymentMethod,
    cardElement,
    idealBankElement,
  }: Omit<ProcessPaymentOptions, 'payment'> & { clientSecret: string },
  lastPaymentAttempt: Date,
) => {
  const { stripe } = paymentProvider
  if (!stripe) {
    throw new Error('Stripe provider was not found')
  }

  const createPaymentMethodOptions = <T, P>(options: T, paymentMethod: P = {} as P) => ({
    ...options,
    payment_method: {
      billing_details: {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      },
      ...paymentMethod,
    },
  })

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD) {
    const options = { save_payment_method: Boolean(selectedPaymentMethod.saveCard.length) }

    // If the user selected a saved card
    if (selectedPaymentMethod.cardId && selectedPaymentMethod.cardId !== NEW_CARD) {
      return stripe.confirmCardPayment(clientSecret, { ...options, payment_method: selectedPaymentMethod.cardId })
    }

    // If the user is using a new card
    if (cardElement) {
      return stripe.confirmCardPayment(clientSecret, createPaymentMethodOptions(options, { card: cardElement }))
    }
  }

  const returnUrlObject = {
    return_url: `${window.location.href}&paymentInProgress=true&lastPaymentAttempt=${lastPaymentAttempt.getTime()}`,
  }

  const trackingParam = getGoogleSearchParam()

  if (trackingParam) {
    const [trackingName, trackingValue] = trackingParam.split('=')
    const returnToAsURL = new URL(returnUrlObject.return_url)
    returnToAsURL.searchParams.append(trackingName, trackingValue)
    returnUrlObject.return_url = returnToAsURL.toString()
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_BANCONTACT) {
    return stripe.confirmBancontactPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_IDEAL && idealBankElement) {
    return stripe.confirmIdealPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject, {
        ideal: idealBankElement,
      }),
    )
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_PAYPAL) {
    return stripe.confirmPayPalPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_SOFORT && selectedPaymentMethod.countryIsoCode) {
    return stripe.confirmSofortPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject, {
        sofort: {
          country: selectedPaymentMethod.countryIsoCode,
        },
      }),
    )
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_EPS) {
    return stripe.confirmEpsPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_GIROPAY) {
    return stripe.confirmGiropayPayment(clientSecret, createPaymentMethodOptions(returnUrlObject))
  }

  if (selectedPaymentMethod.type === PaymentMethodType.PAYMENT_METHOD_TWINT) {
    return (stripe as unknown as { confirmTwintPayment: (typeof stripe)['confirmPayPalPayment'] }).confirmTwintPayment(
      clientSecret,
      createPaymentMethodOptions(returnUrlObject),
    )
  }

  throw new Error('Payment method not implemented')
}

export default confirmPaymentIntent
